import { useHistory } from "react-router-dom";
import Button from "../../organism/Button";
import PatientInfoModel from "../../../services/models/formModels/patientInfo.model";
import useFeatureFlags from "../../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import ButtonProps from "../../../services/models/buttonProps.model";
import dataConstants from "../../../constants/dataConstants";
import CreatePatientPopup from "../../popups/createPatientPopup";
import { useOktaAuth } from "@okta/okta-react";
import { useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PopUpContext } from "../../../services/context/popupContext";
import OrganizationAndClinicianSelection from "../../popups/organizationAndClinicianSelectionPopup";
import useProfile from "../../../services/utils/custom hooks/useProfile";
import { ApplicationState } from "../../../services/context/ApplicationStateContext";

function PatientsActionCell({
  cell,
  row,
  table,
}: {
  cell: any;
  row: any;
  table: any;
}): JSX.Element {
  const history = useHistory();
  const { getActionsForSection } = useFeatureFlags();
  const oktaAuth = useOktaAuth();
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);
  const {
    state: { selectedOrganization, selectedPhysician, selectedPathLab },
    dispatch,
  } = useContext(ApplicationState);
  const queryClient = useQueryClient();
  const { data: profile, isLoading: fetchingProfile } = useProfile();
  const data: PatientInfoModel = {
    id: row.original.id ?? "",
    firstName: row.original.firstName !== "---" ? row.original.firstName : "",
    lastName: row.original.lastName !== "---" ? row.original.lastName : "",
    address: row.original.address !== "---" ? row.original.address : "",
    middleInitial:
      row.original.middleInitial !== "---" ? row.original.middleInitial : "",
    birthDate: row.original.birthDate !== "---" ? row.original.birthDate : "",
    gender: row.original.gender !== "---" ? row.original.gender : "",
    email: row.original.email !== "---" ? row.original.email : "",
    phone: row.original.phone !== "---" ? row.original.phone : "",
    city: row.original.city !== "---" ? row.original.city : "",
    state: row.original.stateCode !== "---" ? row.original.stateCode : "",
    zipcode: row.original.zipcode !== "---" ? row.original.zipcode : "",
    securityInfo: {
      ssn: row.original.ssn !== "---" ? row.original.ssn : "",
      mrn: row.original.mrn !== "---" ? row.original.mrn : "",
    },
  };

  const roles = profile?.user?.roles ?? [];
  const isPhysicianOnly = roles.length === 1 && roles.includes("Physician");
  const buttons: ButtonProps[] = [
    {
      textInformation: {
        text: "Edit",
        fontWeight: "font-semibold",
        fontSize: "text-xs 2xl:text-sm",
      },
      loading: false,
      disabled: false,
      onClick: handleEditClick,
      buttonTheme: "secondary",
      className: "my-1 p-1.5 px-[18px]",
      key: dataConstants.SectionActionKeys.EDIT_PATIENT,
    },
    {
      textInformation: {
        text: "New Test Order",
        fontWeight: "font-semibold",
        fontSize: "text-xs 2xl:text-sm",
      },
      loading: false,
      disabled: false,
      onClick: handleTestOrderClick,
      buttonTheme: "secondary",
      className: "mx-5 my-1 p-1.5 px-[18px]",
      key: dataConstants.SectionActionKeys.PATIENT_NEW_ORDER,
    },
    {
      textInformation: {
        text: "Detail",
        fontWeight: "font-semibold",
        fontSize: "text-xs 2xl:text-sm",
      },
      loading: false,
      disabled: false,
      onClick: detailClick,
      buttonTheme: "secondary",
      className: "p-1.5",
      key: dataConstants.SectionActionKeys.PATIENT_DETAIL,
    },
  ];

  function editPatient(row: any) {
    setVisible(!visible);
    setPopUpTitle(`Update ${row?.original?.firstName}`);
    setPopUp(
      <CreatePatientPopup oktaAuth={oktaAuth} patientInformation={data} />
    );
  }
  function handleEditClick(e: any) {
    e.stopPropagation();
    editPatient(data);
  }
  function handleTestOrderClick(e: any) {
    e.stopPropagation();
    const missingContextValues = !selectedOrganization || !selectedPhysician || (!selectedPathLab && roles?.includes("Collaborative Order Agreement"));
    if (missingContextValues) {
      // When no organization is selected and we have the patient's organizationId (FHIR uuid),
      // pre-select that organization so the popup shows the patient's org (using numeric id).
      const patientFhirOrgId = row?.original?.organizationId;
      if (!selectedOrganization && patientFhirOrgId) {
        const organisations: any = queryClient.getQueryData(["organizations"]);
        const org = organisations?.data?.find(
          (o: any) => o?.fhirOrganizationId === patientFhirOrgId
        );
        if (org) {
          dispatch({ type: "SET_ORGANIZATION", payload: org.id });
          dispatch({
            type: "SET_ORGANIZATION_DATA",
            payload: {
              label: org.name,
              value: org.id,
              address: org.address,
              zipcode: org.zipcode,
              state: org.state,
            },
          });
        }
      }

      setVisible(!visible);
      setPopUpTitle(
        `Select ${roles?.includes("Collaborative Order Agreement") ? "a PathLab" : ""
        } an organization ${isPhysicianOnly ? '' : 'and a clinician'}`
      );
      const organisations: any = queryClient.getQueryData(["organizations"]);
      const patientOrg = organisations?.data?.find(
        (o: any) => o?.fhirOrganizationId === row?.original?.organizationId
      );
      const patientOrganizationId = patientOrg?.id;

      setPopUp(
        <OrganizationAndClinicianSelection
          isPatientActionsCell
          patientId={data.id}
          patientOrganizationId={patientOrganizationId}
          handleNext={(newPatientId?: string) => {
            if (!!data.id) {
              if (newPatientId != null) {
                sessionStorage.setItem(
                  "orderDuplicatePatientId",
                  newPatientId
                );
              }
              const orderPatientInfo =
                newPatientId != null ? { ...data, id: newPatientId } : data;
              history.push(`/access/online-order`, {
                patientInformation: JSON.stringify(orderPatientInfo),
              });
            }
            setVisible(false);
          }}
        />
      );
    } else
      history.push(`/access/online-order`, {
        patientInformation: JSON.stringify(data),
      });
  }

  function detailClick(e: any) {
    e.stopPropagation();
    history.push(`/access/patients/${data.id}`);
  }

  return (
    <div className="overflow-y-auto flex items-center">
      {buttons
        .filter(
          (button) =>
            getActionsForSection(
              dataConstants.FeatureFlagSectionKeys.PATIENTS
            ).find((action) => action.key === button.key)?.isActive
        )
        .map((button) => (
          <Button {...button} />
        ))}
      {/* <Button
        textInformation={{
          text: "Edit",
          fontWeight: "font-semibold",
        }}
        onClick={() => { }}
        buttonTheme="secondary"
        className=" my-1 p-1.5 px-[18px]"
      />
      <Button
        textInformation={{
          text: "New Test Order",
          fontWeight: "font-semibold",
        }}
        onClick={handleTestOrderClick}
        buttonTheme="secondary"
        className="mx-5 my-1 p-1.5 px-[18px]"
      />
      <Button
        textInformation={{
          text: "Detail",
          fontWeight: "font-semibold",
        }}
        onClick={detailClick}
        // onClick={(e) => {
        //   e.stopPropagation();
        //   const id = row.getAllCells()[0].getValue();
        //   history.push(`/access/patients/${id}`);
        // }}
        buttonTheme="secondary"
        className=" p-1.5"
      /> */}
    </div>
  );
}

export default PatientsActionCell;
